import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DataStoreService } from '../../services/data-store';
import { ParsedData, GSCDataSet } from '@taptimize/shared';
import { AnalyticsService, GSCRow, SummaryStats, DateSeries } from '../../services/analytics';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartType } from 'chart.js';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, BaseChartDirective],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.css'
})
export class DashboardComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();
  
  data: ParsedData | null = null;
  availableDatasets: GSCDataSet[] = [];
  currentDataset: GSCDataSet | null = null;
  processedData: GSCRow[] = [];
  filteredData: GSCRow[] = [];
  summaryStats: SummaryStats = { totalClicks: 0, totalImpressions: 0, avgCtr: 0, avgPosition: 0 };
  dateSeriesData: DateSeries[] = [];
  
  // UI State
  activeTab: GSCDataSet['type'] = 'queries';
  searchTerm = '';
  sortBy = 'clicks';
  sortDirection: 'asc' | 'desc' = 'desc';
  currentPage = 1;
  itemsPerPage = 20;
  
  // Date Filter State
  dateFilterType: 'preset' | 'custom' = 'preset';
  selectedPreset: '7days' | '28days' | '3months' | '6months' | '12months' | '16months' = '3months';
  customStartDate: string = '';
  customEndDate: string = '';
  showDatePicker = false;
  
  // Chart configuration
  chartType: ChartType = 'line';
  chartData: ChartConfiguration['data'] = {
    labels: [],
    datasets: []
  };
  
  chartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        position: 'left',
        title: {
          display: true,
          text: 'Clicks'
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Impressions'
        },
        grid: {
          drawOnChartArea: false,
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top'
      },
      tooltip: {
        mode: 'index',
        intersect: false
      }
    }
  };

  constructor(
    private dataStore: DataStoreService,
    private analytics: AnalyticsService
  ) {}

  ngOnInit() {
    this.subscription.add(
      this.dataStore.data$.subscribe(data => {
        if (data) {
          this.data = data;
          this.processData();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private processData() {
    if (!this.data) return;

    // Get available datasets
    this.availableDatasets = this.data.datasets;
    
    // Set initial dataset (prefer queries, then pages, then first available)
    this.currentDataset = this.availableDatasets.find(d => d.type === 'queries') ||
                         this.availableDatasets.find(d => d.type === 'pages') ||
                         this.availableDatasets[0];
    
    if (this.currentDataset) {
      this.activeTab = this.currentDataset.type;
      this.processCurrentDataset();
    }
  }

  private mapDatasetTypeToAnalyticsType(datasetType: GSCDataSet['type']): 'summary' | 'page' | 'query' | 'countries' | 'devices' | 'search_appearance' | 'dates' {
    switch (datasetType) {
      case 'queries': return 'query';
      case 'pages': return 'page';
      case 'dates': return 'dates';
      case 'countries': return 'countries';
      case 'devices': return 'devices';
      case 'search_appearance': return 'search_appearance';
      default: return 'page';
    }
  }

  private processCurrentDataset() {
    if (!this.currentDataset) return;

    // Process the current dataset
    this.processedData = this.analytics.normalizeGSCData(
      this.currentDataset.data, 
      this.mapDatasetTypeToAnalyticsType(this.currentDataset.type)
    );
    
    // Calculate summary stats
    // For accurate totals, use the 'dates' dataset if available, otherwise use current dataset
    const datesDataset = this.availableDatasets.find(d => d.type === 'dates');
    let summaryData: GSCRow[];
    
    if (datesDataset && this.currentDataset.type !== 'dates') {
      // Use dates dataset for accurate summary stats
      summaryData = this.analytics.normalizeGSCData(datesDataset.data, 'summary');
    } else {
      // Use current dataset
      summaryData = this.processedData;
    }
    
    this.summaryStats = this.analytics.calculateSummaryStats(summaryData);
    
    // Generate date series for chart
    this.dateSeriesData = this.analytics.groupDataByDate(summaryData);
    
    // TODO: REMOVE WHEN GSC API IS IMPLEMENTED  
    // Apply date-based estimation for non-dates datasets (CSV imports only)
    if (this.currentDataset.type !== 'dates' && this.dateSeriesData.length > 0) {
      const { startDate, endDate } = this.getDateRange();
      const startDateStr = this.formatDateForComparison(startDate);
      const endDateStr = this.formatDateForComparison(endDate);
      
      // Only apply estimation if the date filter is actually reducing the available data
      if (this.shouldApplyEstimation(startDateStr, endDateStr)) {
        const dataType = this.currentDataset.type === 'queries' ? 'query' : 'page';
        this.processedData = this.analytics.estimateFilteredData(
          this.processedData,
          this.dateSeriesData,
          startDateStr,
          endDateStr,
          dataType
        );
      }
    }
    
    // Apply filters and sorting
    this.applyFiltersAndSort();
    
    // Apply date filtering
    this.filterDataByDateRange();
    
    // Update chart
    this.updateChart();
    
    // Reset pagination
    this.resetPagination();
  }

  private updateChart() {
    if (this.dateSeriesData.length === 0) return;

    this.chartData = {
      labels: this.dateSeriesData.map(d => d.date),
      datasets: [
        {
          label: 'Clicks',
          data: this.dateSeriesData.map(d => d.clicks),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          yAxisID: 'y',
          fill: false,
          tension: 0.3
        },
        {
          label: 'Impressions',
          data: this.dateSeriesData.map(d => d.impressions),
          borderColor: 'rgb(168, 85, 247)',
          backgroundColor: 'rgba(168, 85, 247, 0.1)',
          yAxisID: 'y1',
          fill: false,
          tension: 0.3
        }
      ]
    };
  }

  setActiveTab(tab: GSCDataSet['type']) {
    this.activeTab = tab;
    
    // Switch to the corresponding dataset
    const newDataset = this.availableDatasets.find(d => d.type === tab);
    if (newDataset && newDataset !== this.currentDataset) {
      this.currentDataset = newDataset;
      this.processCurrentDataset();
    }
    
    this.resetPagination();
  }

  onSearch() {
    this.resetPagination();
    this.applyFiltersAndSort();
  }

  onSort(column: string) {
    if (this.sortBy === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column;
      this.sortDirection = 'desc';
    }
    this.resetPagination();
    this.applyFiltersAndSort();
  }

  private applyFiltersAndSort() {
    let filtered = [...this.processedData];
    
    // Filter by search term
    if (this.searchTerm) {
      filtered = this.analytics.filterData(filtered, this.searchTerm);
    }
    
    // Sort data
    filtered = this.analytics.sortData(filtered, this.sortBy, this.sortDirection);
    
    this.filteredData = filtered;
  }

  get paginatedData(): GSCRow[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.filteredData.slice(startIndex, endIndex);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredData.length / this.itemsPerPage);
  }

  get displayingStart(): number {
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get displayingEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.filteredData.length);
  }

  goToPage(page: number) {
    this.currentPage = Math.max(1, Math.min(page, this.totalPages));
  }

  private resetPagination() {
    this.currentPage = 1;
  }

  formatNumber(num: number): string {
    return this.analytics.formatNumber(num);
  }

  formatNumberCompact(num: number, isImpressions: boolean = false): string {
    return this.analytics.formatNumberCompact(num, isImpressions);
  }
  
  formatPercentage(num: number): string {
    return this.analytics.formatPercentage(num);
  }
  
  formatPosition(num: number): string {
    return this.analytics.formatPosition(num);
  }

  getSortIcon(column: string): string {
    if (this.sortBy !== column) return '↕';
    return this.sortDirection === 'asc' ? '↑' : '↓';
  }

  clearData() {
    this.dataStore.clearData();
  }

  getColumnName(): string {
    switch (this.activeTab) {
      case 'queries': return 'query';
      case 'dates': return 'date';
      case 'countries': return 'country';
      case 'devices': return 'device';
      default: return 'page';
    }
  }

  getColumnDisplayName(): string {
    switch (this.activeTab) {
      case 'queries': return 'Top queries';
      case 'pages': return 'Top pages';
      case 'dates': return 'Date';
      case 'countries': return 'Country';
      case 'devices': return 'Device';
      case 'search_appearance': return 'Search Appearance';
      default: return 'Item';
    }
  }

  getCellValue(row: GSCRow): string {
    switch (this.activeTab) {
      case 'queries': return row.query || 'N/A';
      case 'dates': return row.date || 'N/A';
      case 'countries': return (row as any).country || 'N/A';
      case 'devices': return (row as any).device || 'N/A';
      case 'search_appearance': return (row as any).search_appearance || 'N/A';
      default: return row.page || 'N/A';
    }
  }

  // TODO: REMOVE WHEN GSC API IS IMPLEMENTED
  // CSV estimation logic - delete entire method when real-time GSC API is available
  private shouldApplyEstimation(startDateStr: string, endDateStr: string): boolean {
    // Don't estimate if not using any date filter
    if (this.dateFilterType !== 'preset' && this.dateFilterType !== 'custom') {
      return false;
    }

    // Get the full date range from available data
    const allDates = this.dateSeriesData.map(d => d.date).sort();
    if (allDates.length === 0) {
      return false;
    }

    const fullStartDate = allDates[0];
    const fullEndDate = allDates[allDates.length - 1];

    // Only apply estimation if the filter is more restrictive than available data
    const isFilteringStart = startDateStr > fullStartDate;
    const isFilteringEnd = endDateStr < fullEndDate;

    return isFilteringStart || isFilteringEnd;
  }

  // TODO: REMOVE WHEN GSC API IS IMPLEMENTED
  // UI helper for CSV estimation indicator - delete with estimation system
  isShowingEstimatedData(): boolean {
    // Show estimation indicator if we have estimated data
    return this.processedData.length > 0 && 
           (this.processedData[0] as any)?.isEstimated === true;
  }

  // Date Filter Methods
  setDateFilter(preset: '7days' | '28days' | '3months' | '6months' | '12months' | '16months') {
    this.selectedPreset = preset;
    this.dateFilterType = 'preset';
    this.showDatePicker = false; // Close modal when selecting preset
    this.applyDateFilter();
  }

  setCustomDateFilter() {
    this.dateFilterType = 'custom';
    // Don't close modal, allow user to input dates
  }

  isCustomDateFilterActive(): boolean {
    return this.dateFilterType === 'custom';
  }

  setDateFilterToPreset() {
    this.dateFilterType = 'preset';
  }

  applyCustomDateFilter() {
    if (this.customStartDate && this.customEndDate) {
      this.applyDateFilter();
      this.showDatePicker = false; // Close modal after applying
    }
  }
  
  cancelCustomDateFilter() {
    this.showDatePicker = false;
    // Reset to previous state if needed
    if (this.dateFilterType === 'custom' && (!this.customStartDate || !this.customEndDate)) {
      this.dateFilterType = 'preset';
    }
  }

  private applyDateFilter() {
    if (!this.data) return;

    // Reprocess the current dataset to get fresh data
    this.processCurrentDataset();
  }

  getDateFilterDisplayText(): string {
    if (this.dateFilterType === 'custom') {
      return `${this.customStartDate} - ${this.customEndDate}`;
    }
    
    const { startDate, endDate } = this.getDateRange();
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  }

  private getDateRange(): { startDate: Date; endDate: Date } {
    let startDate: Date;
    let endDate: Date;
    
    // Get the actual last date from the data instead of using today
    if (this.dateSeriesData && this.dateSeriesData.length > 0) {
      // Find the latest date in the actual data
      const latestDataDate = this.dateSeriesData
        .map(d => d.date)
        .sort()
        .pop();
      
      if (latestDataDate) {
        endDate = new Date(latestDataDate + 'T00:00:00'); // Convert YYYY-MM-DD to Date
      } else {
        // Fallback to current date if no data
        endDate = new Date();
      }
    } else {
      // Fallback to current date if no data loaded yet
      endDate = new Date();
    }

    if (this.dateFilterType === 'preset') {
      switch (this.selectedPreset) {
        case '7days':
          // 7 days: June 24 → June 18 (7 days total)
          startDate = new Date(endDate);
          startDate.setDate(startDate.getDate() - 6);
          break;
        case '28days':
          // 28 days: June 24 → May 28 (28 days total)
          startDate = new Date(endDate);
          startDate.setDate(startDate.getDate() - 27);
          break;
        case '3months':
          // 3 months: June 24 → March 25 (92 days total)
          startDate = new Date(endDate);
          startDate.setDate(startDate.getDate() - 91); // 92 days inclusive = 91 days back
          break;
        case '6months':
          startDate = new Date(endDate);
          startDate.setDate(startDate.getDate() - 179); // 180 days inclusive
          break;
        case '12months':
          startDate = new Date(endDate);
          startDate.setDate(startDate.getDate() - 364); // 365 days inclusive
          break;
        case '16months':
          startDate = new Date(endDate);
          startDate.setDate(startDate.getDate() - 486); // ~16 months inclusive
          break;
        default:
          startDate = new Date(endDate);
          startDate.setDate(startDate.getDate() - 91);
      }
    } else {
      startDate = new Date(this.customStartDate);
      endDate = new Date(this.customEndDate);
    }

    return { startDate, endDate };
  }

  private filterDataByDateRange() {
    const { startDate, endDate } = this.getDateRange();
    
    // Convert dates to strings for comparison (YYYY-MM-DD format)
    const startDateStr = this.formatDateForComparison(startDate);
    const endDateStr = this.formatDateForComparison(endDate);

    // Filter date series data using string comparison for accuracy
    this.dateSeriesData = this.dateSeriesData.filter(d => {
      const dateStr = d.date; // Already in YYYY-MM-DD format
      return dateStr >= startDateStr && dateStr <= endDateStr;
    });

    // Update summary stats based on filtered date series
    this.updateSummaryStatsFromFilteredData();
  }

  private formatDateForComparison(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private updateSummaryStatsFromFilteredData() {
    if (this.dateSeriesData.length === 0) {
      this.summaryStats = { totalClicks: 0, totalImpressions: 0, avgCtr: 0, avgPosition: 0 };
      return;
    }

    const totalClicks = this.dateSeriesData.reduce((sum, d) => sum + d.clicks, 0);
    const totalImpressions = this.dateSeriesData.reduce((sum, d) => sum + d.impressions, 0);
    const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    const avgPosition = this.dateSeriesData.reduce((sum, d) => sum + d.position, 0) / this.dateSeriesData.length;

    this.summaryStats = {
      totalClicks,
      totalImpressions,
      avgCtr,
      avgPosition
    };
  }
} 